import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminRequests } from "@/hooks/useAdminRequests";
import { RequestList } from "@/components/admin/requests/RequestList";
import RequestsHeader from "@/components/admin/requests/RequestsHeader";
import RequestsLoading from "@/components/admin/requests/RequestsLoading";
import { RequestsErrorBoundary } from "@/components/admin/requests/RequestsErrorBoundary";
import { GroupLeaderOverview } from "@/components/admin/requests/GroupLeaderOverview";
import { setupSessionTimeout } from "@/utils/sessionManager";
import { secureSignOut } from "@/utils/authUtils";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminRequests = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    requests,
    isLoading,
    processingId,
    checkAuth,
    fetchRequests,
    handleApprove,
    handleReject,
    handleDelete,
    handleSendInvitation,
    handleResetPassword
  } = useAdminRequests();

  // Set up session timeout
  useEffect(() => {
    const cleanup = setupSessionTimeout(async () => {
      await secureSignOut();
      toast({
        title: "Session Timeout",
        description: "You have been logged out due to inactivity.",
        variant: "destructive",
      });
      navigate("/admin");
    });

    return cleanup;
  }, [navigate, toast]);

  useEffect(() => {
    const initialize = async () => {
      // Check admin-specific permissions (this also checks basic authentication)
      const isAuthorized = await checkAuth();
      
      // If authorized, fetch the requests
      if (isAuthorized) {
        fetchRequests();
      }
    };
    
    initialize();
  }, [navigate, checkAuth, fetchRequests]);

  if (isLoading) {
    return <RequestsLoading />;
  }

  return (
    <div className="min-h-screen p-4 bg-background">
      <div className="max-w-6xl mx-auto space-y-8">
        <RequestsHeader />

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Översikt gruppledare</TabsTrigger>
            <TabsTrigger value="requests">Förfrågningar</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <GroupLeaderOverview />
          </TabsContent>

          <TabsContent value="requests">
            <RequestsErrorBoundary onRetry={fetchRequests}>
              <RequestList
                requests={requests}
                processingId={processingId}
                onApprove={handleApprove}
                onReject={handleReject}
                onDelete={handleDelete}
                onSendInvitation={handleSendInvitation}
                onResetPassword={handleResetPassword}
              />
            </RequestsErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminRequests;
